import ComicCard from '@/components/feature/ComicCard';
import Layout from '@/components/feature/Layout';
import { getFeaturedComics, getLatestUpdates, getPopularComics } from '@/mocks/comics';
import { categories } from '@/mocks/categories';
import { siteConfig, navLinks } from '@/mocks/siteConfig';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: siteConfig.name,
  url: siteConfig.url,
  description: siteConfig.description,
  foundingDate: siteConfig.founded,
  contactPoint: {
    '@type': 'ContactPoint',
    email: siteConfig.email,
    contactType: 'customer service',
  },
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: siteConfig.name,
  url: siteConfig.url,
  description: siteConfig.description,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${siteConfig.url}/search?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '韩漫画网上的漫画是免费的吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '是的！韩漫画网所有韩国漫画资源均免费提供在线阅读，无需付费即可享受下拉式阅读体验，随时随地畅读韩国漫画大全。',
      },
    },
    {
      '@type': 'Question',
      name: '韩漫画网支持哪些设备阅读？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '韩漫画网支持电脑、平板和手机等多种设备。我们的下拉式阅读模式在所有设备上都能流畅运行，让您的韩漫在线观看体验更加舒适。',
      },
    },
    {
      '@type': 'Question',
      name: '韩漫画网多久更新一次漫画？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '韩漫画网每日更新热门韩国漫画，连载中的作品会及时同步最新章节，确保读者能在第一时间追更喜爱的韩漫作品。',
      },
    },
    {
      '@type': 'Question',
      name: '如何在韩漫画网上找到想看的漫画？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '您可以通过韩漫画网首页的分类导航按恋爱、动作、奇幻、悬疑等类型筛选漫画，也可以直接使用搜索功能或浏览热门推荐找到您感兴趣的韩国漫画。',
      },
    },
  ],
};

export default function Home() {
  const featured = getFeaturedComics();
  const latest = getLatestUpdates();
  const popular = getPopularComics();

  return (
    <Layout>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {/* Hero Section */}
      <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20reading%20platform%20hero%20background%2C%20artistic%20abstract%20illustration%20with%20warm%20orange%20and%20deep%20brown%20gradients%2C%20subtle%20comic%20panel%20textures%2C%20soft%20geometric%20shapes%2C%20modern%20digital%20art%20aesthetic%2C%20clean%20minimal%20design%2C%20no%20text%2C%20atmospheric%20lighting&width=1600&height=900&seq=hero-bg-home&orientation=landscape"
          alt="韩漫画网 - 下拉式免费漫画阅读平台"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/40 via-foreground-950/20 to-foreground-950/60" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-4 max-w-3xl">
            <h1 className="text-3xl md:text-5xl font-bold font-heading text-background-50 mb-4 leading-tight">
              <strong>{siteConfig.name}</strong>
            </h1>
            <p className="text-base md:text-xl text-background-50/90 mb-8 max-w-xl mx-auto leading-relaxed">
              下拉式免费漫画阅读平台，涵盖韩国漫画大全资源<br />
              操作简单，内容全面，您的韩漫在线观看首选
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/category/romance"
                className="bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3 rounded-full font-semibold text-sm transition-all whitespace-nowrap"
              >
                开始阅读
              </a>
              <a
                href="/category/action"
                className="bg-background-50/15 hover:bg-background-50/25 backdrop-blur-sm text-background-50 px-8 py-3 rounded-full font-semibold text-sm transition-all whitespace-nowrap border border-background-50/30"
              >
                热门推荐
              </a>
            </div>
            <div className="flex items-center justify-center gap-6 mt-8 text-background-50/70 text-xs">
              <span className="flex items-center gap-1.5">
                <i className="ri-book-read-line"></i> 下拉式阅读
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-shield-check-line"></i> 完全免费
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-smartphone-line"></i> 多端适配
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 -mt-8 relative z-10">
        <div className="bg-background-50 rounded-xl p-4 md:p-5 border border-background-200/70">
          <div className="flex items-center gap-6 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map((cat) => (
              <a
                key={cat.slug}
                href={`/category/${cat.slug}`}
                className="flex flex-col items-center gap-2 group min-w-[80px] flex-shrink-0"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-background-100 group-hover:bg-primary-500 transition-colors duration-300">
                  <i className={`${cat.icon} text-xl text-foreground-700 group-hover:text-background-50 transition-colors`}></i>
                </div>
                <span className="text-xs font-medium text-foreground-700 group-hover:text-primary-500 transition-colors whitespace-nowrap">
                  {cat.name}
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Hot Comics - Featured */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
              热门推荐
            </h2>
            <p className="text-sm text-foreground-500 mt-1">精选高分韩国漫画，口碑之选</p>
          </div>
          <a href="/category/action" className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap flex items-center gap-1">
            查看更多 <i className="ri-arrow-right-line"></i>
          </a>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {featured.map((comic) => (
            <ComicCard key={comic.id} comic={comic} />
          ))}
        </div>
      </section>

      {/* Popular Rankings */}
      <section className="bg-background-100 py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
                人气排行
              </h2>
              <p className="text-sm text-foreground-500 mt-1">最受欢迎的韩国漫画，千万读者的选择</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {popular.map((comic, index) => (
              <a
                key={comic.id}
                href={`/article/${comic.id}`}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="flex gap-4 p-4 bg-background-50 rounded-lg hover:bg-background-200/50 transition-colors group"
              >
                <div className={`w-10 h-10 flex items-center justify-center rounded-full text-background-50 font-bold text-sm flex-shrink-0 ${
                  index < 3 ? 'bg-primary-500' : 'bg-foreground-400'
                }`}>
                  {index + 1}
                </div>
                <div className="flex-shrink-0 w-14 h-20 rounded overflow-hidden">
                  <img src={comic.cover} alt={comic.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm text-foreground-950 group-hover:text-primary-500 transition-colors truncate">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-0.5">{comic.author}</p>
                  <div className="flex items-center gap-3 mt-1.5">
                    <span className="flex items-center gap-0.5 text-xs text-accent-600">
                      <i className="ri-star-fill text-xs"></i>
                      {comic.rating}
                    </span>
                    <span className="text-xs text-foreground-400">{comic.views}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                      comic.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'
                    }`}>
                      {comic.status}
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Updates */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
              最新更新
            </h2>
            <p className="text-sm text-foreground-500 mt-1">每日同步更新，不错过任何精彩章节</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {latest.map((comic) => (
            <ComicCard key={comic.id} comic={comic} />
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="bg-background-100 py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 text-center mb-8">
            为什么选择韩漫画网？
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: 'ri-download-cloud-line', title: '下拉式阅读', desc: '独特的滚动阅读方式，无需翻页，一气呵成读完每一话' },
              { icon: 'ri-money-cny-circle-line', title: '完全免费', desc: '所有漫画资源免费阅读，无需付费订阅即可畅享韩国漫画大全' },
              { icon: 'ri-refresh-line', title: '每日更新', desc: '连载作品每日同步更新，让您第一时间阅读最新话' },
              { icon: 'ri-device-line', title: '多端适配', desc: '支持电脑、平板和手机，随时随地畅享韩漫在线观看' },
            ].map((feature) => (
              <div key={feature.title} className="bg-background-50 rounded-lg p-5 text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                  <i className={`${feature.icon} text-xl text-primary-500`}></i>
                </div>
                <h3 className="font-semibold text-sm text-foreground-950 mb-2">{feature.title}</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 text-center mb-8">
          常见问题
        </h2>
        <div className="space-y-3">
          {[
            { q: '韩漫画网上的漫画是免费的吗？', a: '是的！韩漫画网所有韩国漫画资源均免费提供在线阅读，无需付费即可享受下拉式阅读体验，随时随地畅读韩国漫画大全。' },
            { q: '韩漫画网支持哪些设备阅读？', a: '韩漫画网支持电脑、平板和手机等多种设备。我们的下拉式阅读模式在所有设备上都能流畅运行，让您的韩漫在线观看体验更加舒适。' },
            { q: '韩漫画网多久更新一次漫画？', a: '韩漫画网每日更新热门韩国漫画，连载中的作品会及时同步最新章节，确保读者能在第一时间追更喜爱的韩漫作品。' },
            { q: '如何在韩漫画网上找到想看的漫画？', a: '您可以通过韩漫画网首页的分类导航按恋爱、动作、奇幻、悬疑等类型筛选漫画，也可以直接使用搜索功能或浏览热门推荐找到您感兴趣的韩国漫画。' },
          ].map((faq, idx) => (
            <details key={idx} className="group bg-background-50 rounded-lg border border-background-200/70">
              <summary className="flex items-center justify-between p-4 cursor-pointer">
                <span className="font-medium text-sm text-foreground-950">{faq.q}</span>
                <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="px-4 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative w-full py-16 md:py-20 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20reading%20atmosphere%2C%20warm%20cozy%20reading%20environment%20with%20soft%20orange%20and%20cream%20tones%2C%20abstract%20artistic%20background%20with%20subtle%20line%20art%20textures%2C%20modern%20digital%20illustration%2C%20clean%20minimal%20design%2C%20no%20text%2C%20comfortable%20ambiance&width=1600&height=600&seq=cta-bg-home&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-primary-500/80" />
        <div className="relative max-w-3xl mx-auto text-center px-4">
          <h2 className="text-2xl md:text-3xl font-bold font-heading text-background-50 mb-4">
            立即开始您的韩漫之旅
          </h2>
          <p className="text-background-50/90 mb-8 max-w-lg mx-auto text-sm leading-relaxed">
            加入数百万读者，在韩漫画网享受免费、流畅的韩国漫画在线阅读体验
          </p>
          <a
            href="/category/romance"
            className="inline-block bg-background-50 text-primary-600 px-8 py-3 rounded-full font-semibold text-sm hover:bg-background-100 transition-all whitespace-nowrap"
          >
            立即免费阅读
          </a>
        </div>
      </section>
    </Layout>
  );
}